// Push review wireframe data, built from the VN inventory sheet (sheetData.ts).

import { SHEET_BATCH_ROWS, SHEET_PRODUCTS } from "./sheetData"

export const ACTION_TYPES = [
  "Gifting",
  "Flash sale",
  "KOL booking",
  "In-house livestream",
  "Marketplace campaign",
  "Price change",
  "Move stock",
  "Purchase order",
  "Other",
] as const

export type ActionType = (typeof ACTION_TYPES)[number]

export const CHANNELS = ["All channels", "Shopee", "TikTok Shop", "Lazada", "Wholesale", "Retail", "Ecom"]

export interface PushAction {
  id: string
  type: ActionType
  note: string
  channel: string | null
  from: string | null // ISO yyyy-mm-dd
  to: string | null // ISO yyyy-mm-dd, same as from for a one-day action
  pics: string[]
}

export interface TriedAction {
  type: ActionType
  date: string // ISO yyyy-mm-dd, the day it ran
}

export interface Incoming {
  qty: number
  arrives: string // ISO yyyy-mm-dd
}

// One product as the sheet has it. Units out means sold plus gifted.
export interface PushProduct {
  id: string
  sku: string // Lixibox ID, used for #mentions
  code: string | null // the team's short product code, e.g. HRI-P
  name: string
  shortName: string | null // Atlas products.short_name, e.g. HSL-OB
  category: string
  giftable: boolean
  stock: number
  stockByWarehouse: number[] // in WAREHOUSES order (stock.ts)
  out60: number[] // units out in 60 days through MKP, Ecom, Wholesale, Retail
  outPerDay: number
  coverDays: number | null // null when nothing moves
  costPerUnit: number // VND, average buying price
  highDoc: boolean
  incoming: Incoming | null
  weeklySold: number[] // per week in SALES_WEEKS
  weeklyGifted: number[]
  tried: TriedAction[]
}

export const PEOPLE = ["Ann Nguyen", "Bao Tran", "Chi Le", "Duc Pham", "Hoa Vu", "Linh Do"]

// Past actions from the Sep 17 meeting's expiry list ("Move to MKP",
// "MKP clearance"), dated the meeting day.
export function triedFrom(note: string | null): ActionType[] {
  if (!note) return []
  const types: ActionType[] = []
  if (/move to mkp/i.test(note)) types.push("Move stock")
  if (/clearance/i.test(note)) types.push("Marketplace campaign")
  return types
}

const SEP_17 = "2026-09-17"

const triedBySku = new Map<string, TriedAction[]>()
for (const row of SHEET_BATCH_ROWS) {
  const list = triedBySku.get(row.lixibox) ?? []
  for (const type of triedFrom(row.actionSep17)) {
    if (!list.some((a) => a.type === type)) list.push({ type, date: SEP_17 })
  }
  triedBySku.set(row.lixibox, list)
}

// The sheet gives no arrival date for incoming orders; every order lands on
// this made-up day.
const ARRIVES = "2026-11-15"

export const PRODUCTS: PushProduct[] = SHEET_PRODUCTS.map((p) => {
  const stock = p.stock.reduce((a, b) => a + b, 0)
  const out = p.out60.reduce((a, b) => a + b, 0)
  const outPerDay = Math.round((out / 60) * 10) / 10
  return {
    id: p.lixibox,
    sku: p.lixibox,
    code: p.code,
    name: p.name,
    shortName: p.shortName,
    category: p.category,
    giftable: p.isGift || p.gifted.some((n) => n > 0),
    stock,
    stockByWarehouse: p.stock,
    out60: p.out60,
    outPerDay,
    coverDays: outPerDay > 0 ? Math.round(stock / outPerDay) : null,
    costPerUnit: p.cost ?? 0,
    highDoc: p.highDoc,
    incoming: p.incoming > 0 ? { qty: p.incoming, arrives: ARRIVES } : null,
    weeklySold: p.sold,
    weeklyGifted: p.gifted,
    tried: triedBySku.get(p.lixibox) ?? [],
  }
})

// The name for the agenda list: the short name when Atlas has one, otherwise
// the full name without its leading bracket tags such as "[CO-BRAND]".
export const listName = (p: PushProduct) => p.shortName ?? p.name.replace(/^\s*(\[[^\]]*\]\s*)+/, "").trim()

export const productBySku = (sku: string) => PRODUCTS.find((p) => p.sku === sku)

// High DOC tabs, in the sheet's category order.
export const CATEGORIES = [...new Set(PRODUCTS.filter((p) => p.highDoc).map((p) => p.category))]

// What the Oct 19 meeting decided, already typed in. Notes are translated
// from the meeting sheet.
const action = (id: string, type: ActionType, note: string, channel: string | null, from: string | null, to: string | null, pics: string[]): PushAction =>
  ({ id, type, note, channel, from, to, pics })

export const STARTING_ACTIONS: Record<string, PushAction[]> = {
  // HSG
  LXE9CA89D01A: [
    action("a1", "Price change", "Tell wholesale this week that the price goes up", "Wholesale", "2026-10-19", "2026-10-25", ["Bao Tran"]),
    action("a2", "Price change", "Andy: keep the price of #LX8D4BE32768 (HRI-P) well apart from HSG", "All channels", null, null, ["Ann Nguyen"]),
  ],
  // HRI-P
  LX8D4BE32768: [
    action("a3", "Marketplace campaign", "Focus on HRI Pink until 11.11", "TikTok Shop", "2026-10-19", "2026-11-11", ["Chi Le"]),
  ],
  // HRI-B
  LX6A8C0F0609: [
    action("a4", "Marketplace campaign", "Focus on HRI Beige until 11.11", "Shopee", "2026-10-19", "2026-11-11", ["Chi Le"]),
    action("a11", "Flash sale", "Sale days Oct 20 and Oct 25", "Shopee", "2026-10-20", "2026-10-25", ["Chi Le"]),
  ],
  // HRC
  LX56FFAD3D93: [
    action("a12", "Other", "Push through wholesale", "Wholesale", null, null, ["Bao Tran"]),
  ],
  // HRT
  LXD80164117D: [
    action("a5", "Gifting", "Free with every #LX85565927CD (HRM)", "All channels", null, null, ["Hoa Vu"]),
    action("a6", "Other", "11.11 at Vạn Hạnh Mall (plan)", "Retail", "2026-11-11", "2026-11-11", ["Linh Do"]),
  ],
  // HRW
  LXAD60F28DFB: [
    action("a7", "Marketplace campaign", "Focus on Shopee", "Shopee", null, null, ["Hoa Vu"]),
  ],
  // Chacott Cleansing Water
  LX7247934920: [
    action("a8", "Purchase order", "Stop the January 2027 order, focus on wholesale", "Wholesale", null, null, ["Duc Pham"]),
    action("a9", "Move stock", "Move 10,000 from Viettel Post - Master to Boxme MKP for Shopee", "Shopee", null, null, ["Duc Pham"]),
  ],
  // HIC
  LXEE4670D74F: [
    action("a10", "Gifting", "Idea: buy 3 single hair perfumes, get 1 HIC free", null, null, null, []),
  ],
}

export const valueOf = (product: PushProduct) => product.stock * product.costPerUnit

export function monthsLabel(months: number) {
  return `${months} ${months === 1 ? "month" : "months"}`
}

export function num(value: number) {
  return value.toLocaleString("en-US")
}
